import React, { ReactNode, useMemo } from 'react';
import { MobileMode, MobileThemeProvider, ThemeProvider } from '@amzn/storm-ui';
import { StyleSheetManager } from 'styled-components';
import { connect } from 'react-redux';
import rtlPlugin from 'stylis-plugin-rtl';
import { ErrorBoundary } from '../ErrorBoundary';
import { RootState } from 'src/app/store';
import { selectViewType, VIEW_TYPE } from 'src/app/slice';
import { getExtendedTheme } from './ChromeAppUtils';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';

// This noop plugin is required to change hasing algo for styled component
// and is need to prevent classname collisions
// eslint-disable-next-line @typescript-eslint/no-empty-function
function NoopPlugin() {}

Object.defineProperty(NoopPlugin, 'name', { value: 'aac-ccx' });

// The common HOC for Chrome App
interface ChromeAppProps {
  name: string;
  children: ReactNode;
  mobileView?: boolean;
  viewType: VIEW_TYPE;
}

export const ChromeApp: React.FC<ChromeAppProps> = ({
  name,
  mobileView,
  children,
  viewType,
}) => {
  const theme = useMemo(() => getExtendedTheme(viewType), [viewType]);

  const { loadingStatus } = useInternalChromeConfigContext();
  if (loadingStatus === 'loading') return null;

  return (
    <ErrorBoundary name={name} mobileView={mobileView}>
      <ThemeProvider theme={theme}>
        <MobileThemeProvider
          mobileMode={
            viewType === VIEW_TYPE.MOBILE
              ? MobileMode.Enable
              : MobileMode.Disable
          }
        >
          <StyleSheetManager
            stylisPlugins={
              document.documentElement.dir === 'rtl'
                ? [rtlPlugin, NoopPlugin]
                : [NoopPlugin]
            }
          >
            {children}
          </StyleSheetManager>
        </MobileThemeProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

const connector = connect((state: RootState, ownProps) => {
  return {
    ...ownProps,
    viewType: selectViewType(state),
  };
});

export const ConnectedChromeApp = connector(ChromeApp);
